import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button, Input, message, Tag, Tooltip, Typography } from "antd";
import { CloseOutlined, CustomerServiceOutlined, SendOutlined } from "@ant-design/icons";
import moment from "moment";
import { api } from "../../services/apiClient";

const { Text } = Typography;
const POSITION_KEY = "autoCreatorFeedbackPosition";
const FLOAT_SIZE = 58;
const EDGE_GAP = 8;

function formatTime(value) {
  if (!value) return "";
  const date = moment(value);
  return date.isValid() ? date.format("DD/MM/YYYY HH:mm") : "";
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function loadSavedPosition() {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(POSITION_KEY));
    if (typeof parsed?.left === "number" && typeof parsed?.top === "number") {
      return {
        left: clamp(parsed.left, EDGE_GAP, window.innerWidth - FLOAT_SIZE - EDGE_GAP),
        top: clamp(parsed.top, EDGE_GAP, window.innerHeight - FLOAT_SIZE - EDGE_GAP),
      };
    }
  } catch (_) {
    // Keep the default bottom-right position when local storage is unavailable.
  }
  return null;
}

function FeedbackMessage({ item }) {
  if (item.messageType === "NOTIFICATION") {
    return (
      <div className="feedback-bubble feedback-bubble-admin feedback-bubble-notification">
        <div className="feedback-bubble-meta">
          <Text strong>Auto Upload</Text>
          <Text type="secondary">{formatTime(item.createdAt)}</Text>
        </div>
        <Text strong>{item.message}</Text>
      </div>
    );
  }

  return (
    <div className="feedback-thread-item">
      <div className="feedback-bubble feedback-bubble-user">
        <div className="feedback-bubble-meta">
          <Text strong>Bạn</Text>
          <Text type="secondary">{formatTime(item.createdAt)}</Text>
        </div>
        <Text>{item.message}</Text>
      </div>
      {item.adminReply ? (
        <div className="feedback-bubble feedback-bubble-admin">
          <div className="feedback-bubble-meta">
            <Text strong>Auto Upload</Text>
            <Text type="secondary">{formatTime(item.repliedAt)}</Text>
          </div>
          <Text>{item.adminReply}</Text>
        </div>
      ) : (
        <Tag color="gold">Đang chờ phản hồi</Tag>
      )}
    </div>
  );
}

export default function FeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [position, setPosition] = useState(() => loadSavedPosition());
  const threadRef = useRef(null);
  const dragRef = useRef(null);
  const dragMovedRef = useRef(false);

  const sortedItems = useMemo(
    () => [...items].sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0)),
    [items]
  );

  const scrollToLatest = useCallback(() => {
    window.requestAnimationFrame(() => {
      if (threadRef.current) {
        threadRef.current.scrollTop = threadRef.current.scrollHeight;
      }
    });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems((await api.get("/api/feedbacks/mine")) || []);
    } catch (_) {
      // The main API client handles auth state; keep the floating widget quiet.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    load();
    const timer = window.setInterval(load, 30000);
    return () => window.clearInterval(timer);
  }, [load, open]);

  useEffect(() => {
    if (open) {
      scrollToLatest();
    }
  }, [open, scrollToLatest, sortedItems.length]);

  useEffect(() => {
    const handleResize = () => {
      setPosition((current) => {
        if (!current) return current;
        return {
          left: clamp(current.left, EDGE_GAP, window.innerWidth - FLOAT_SIZE - EDGE_GAP),
          top: clamp(current.top, EDGE_GAP, window.innerHeight - FLOAT_SIZE - EDGE_GAP),
        };
      });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const startDrag = (event) => {
    if (event.button !== 0) return;
    const rect = event.currentTarget.getBoundingClientRect();
    dragMovedRef.current = false;
    dragRef.current = {
      pointerId: event.pointerId,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      startX: event.clientX,
      startY: event.clientY,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const moveDrag = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const distance = Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY);
    if (distance > 3) {
      dragMovedRef.current = true;
    }
    const next = {
      left: clamp(event.clientX - drag.offsetX, EDGE_GAP, window.innerWidth - FLOAT_SIZE - EDGE_GAP),
      top: clamp(event.clientY - drag.offsetY, EDGE_GAP, window.innerHeight - FLOAT_SIZE - EDGE_GAP),
    };
    setPosition(next);
  };

  const endDrag = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    dragRef.current = null;
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
      if (position || dragMovedRef.current) {
        const rect = event.currentTarget.getBoundingClientRect();
        window.localStorage.setItem(POSITION_KEY, JSON.stringify({ left: rect.left, top: rect.top }));
      }
    } catch (_) {
      // Ignore storage/pointer release errors; dragging is still usable for this session.
    }
  };

  const toggleOpen = () => {
    if (dragMovedRef.current) {
      dragMovedRef.current = false;
      return;
    }
    setOpen((value) => !value);
  };

  const send = async () => {
    if (!content.trim()) {
      message.warning("Nhập nội dung lỗi cần phản ánh");
      return;
    }
    setSending(true);
    try {
      await api.post("/api/feedbacks", { message: content.trim() });
      setContent("");
      message.success("Đã gửi phản ánh");
      await load();
      scrollToLatest();
    } catch (error) {
      message.error(error.message);
    } finally {
      setSending(false);
    }
  };

  const unread = items.filter((item) => item.status === "ANSWERED" || item.messageType === "NOTIFICATION").length;
  const widgetStyle = position ? { left: position.left, top: position.top, right: "auto", bottom: "auto" } : undefined;

  return (
    <div className="feedback-widget" style={widgetStyle}>
      {open && (
        <div className="feedback-panel">
          <div className="feedback-panel-header">
            <div>
              <Text strong>Phản ánh lỗi</Text>
              <Text type="secondary">Gửi lỗi và xem phản hồi từ quản trị.</Text>
            </div>
            <Tooltip title="Đóng">
              <Button
                aria-label="Đóng phản ánh lỗi"
                className="feedback-close-button"
                icon={<CloseOutlined />}
                onClick={() => setOpen(false)}
                type="text"
              />
            </Tooltip>
          </div>
          <div className="feedback-thread" ref={threadRef}>
            {sortedItems.length === 0 && !loading ? <Text type="secondary">Chưa có phản ánh nào.</Text> : null}
            {sortedItems.map((item) => (
              <FeedbackMessage item={item} key={item.id} />
            ))}
          </div>
          <div className="feedback-input">
            <Input.TextArea
              autoSize={{ minRows: 2, maxRows: 4 }}
              disabled={sending}
              onChange={(event) => setContent(event.target.value)}
              onPressEnter={(event) => {
                if (!event.shiftKey) {
                  event.preventDefault();
                  send();
                }
              }}
              placeholder="Nhập nội dung lỗi..."
              value={content}
            />
            <Tooltip title="Gửi phản ánh">
              <Button
                aria-label="Gửi phản ánh"
                className="feedback-send-button"
                icon={<SendOutlined />}
                loading={sending}
                onClick={send}
                type="text"
              />
            </Tooltip>
          </div>
        </div>
      )}
      <Tooltip title="Phản ánh lỗi">
        <Badge count={unread} offset={[-6, 6]}>
          <Button
            aria-label="Mở phản ánh lỗi"
            className="feedback-float-button"
            icon={<CustomerServiceOutlined />}
            onClick={toggleOpen}
            onPointerDown={startDrag}
            onPointerMove={moveDrag}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            type="primary"
          />
        </Badge>
      </Tooltip>
    </div>
  );
}
